import { Controller } from '@hotwired/stimulus'
import { Turbo } from '@hotwired/turbo-rails'

export default class extends Controller {
  static targets = [
    'domainId', 'trigger', 'label', 'panel', 'list',
    'addTrigger', 'addForm', 'addInput', 'slug', 'url', 'option'
  ]

  static values = {
    accountPath: String,
    endpointPrefix: String
  }

  connect () {
    this.closeOnOutside = (event) => {
      if (!this.element.contains(event.target)) this.closePanel()
    }
    this.closeOnEscape = (event) => {
      if (event.key === 'Escape') this.closePanel()
    }
    document.addEventListener('click', this.closeOnOutside)
    document.addEventListener('keydown', this.closeOnEscape)

    this.refreshSelection()
    this.syncUrl()
  }

  disconnect () {
    document.removeEventListener('click', this.closeOnOutside)
    document.removeEventListener('keydown', this.closeOnEscape)
  }

  // Panel open/close

  toggle () {
    this.panelTarget.classList.toggle('hidden')
  }

  closePanel () {
    this.panelTarget.classList.add('hidden')
    this.hideAdd()
  }

  // Selecting a saved domain

  select (event) {
    const row = event.target.closest('[data-domain-id]')
    if (!row) return

    this.domainIdTarget.value = row.dataset.domainId
    this.refreshSelection()
    this.syncUrl()
    this.closePanel()
    this.save()
  }

  refreshSelection () {
    const selectedId = this.domainIdTarget.value
    let hostname = ''

    this.optionTargets.forEach((row) => {
      const isSelected = row.dataset.domainId === selectedId
      const check = row.querySelector('[data-role="check"]')
      if (check) check.classList.toggle('hidden', !isSelected)
      if (isSelected) hostname = row.dataset.hostname
    })

    this.labelTarget.textContent = hostname || 'Select a domain'
    this.labelTarget.classList.toggle('text-gray-400', !hostname)
    this.updateCnameHost(hostname)
  }

  // Add a new domain

  showAdd () {
    this.addTriggerTarget.classList.add('hidden')
    this.addFormTarget.classList.remove('hidden')
    this.addInputTarget.focus()
  }

  hideAdd () {
    this.addFormTarget.classList.add('hidden')
    this.addTriggerTarget.classList.remove('hidden')
    this.addInputTarget.value = ''
  }

  // Inline rename

  startEdit (event) {
    const row = event.target.closest('[data-domain-id]')
    this.setRowEditing(row, true)
    const input = row.querySelector('[data-role="edit-input"]')
    input.focus()
    input.select()
  }

  cancelEdit (event) {
    const row = event.target.closest('[data-domain-id]')
    row.querySelector('[data-role="edit-input"]').value = row.dataset.hostname
    this.setRowEditing(row, false)
  }

  async saveEdit (event) {
    event.preventDefault()
    const row = event.target.closest('[data-domain-id]')
    const hostname = row.querySelector('[data-role="edit-input"]').value.trim()
    if (!hostname) return

    const response = await this.request('PATCH', this.memberPath(row.dataset.domainId), { hostname })

    if (await this.renderStream(response)) {
      this.refreshSelection()
      this.syncUrl()
    }
  }

  // Delete

  async destroy (event) {
    const row = event.target.closest('[data-domain-id]')
    const wasSelected = row.dataset.domainId === this.domainIdTarget.value

    const response = await this.request('DELETE', this.memberPath(row.dataset.domainId))

    if (await this.renderStream(response) && wasSelected) {
      this.domainIdTarget.value = ''
      this.refreshSelection()
      this.syncUrl()
      this.save()
    }
  }

  // Endpoint + copy URL

  syncUrl () {
    const hostname = this.selectedHostname()
    if (!hostname) {
      this.urlTarget.textContent = ''
      return
    }
    this.urlTarget.textContent = `https://${hostname}${this.endpointPrefixValue}${this.slugTarget.value}`
  }

  // Private

  setRowEditing (row, editing) {
    row.querySelector('[data-role="display"]').classList.toggle('hidden', editing)
    row.querySelector('[data-role="edit"]').classList.toggle('hidden', !editing)
  }

  selectedHostname () {
    const selectedId = this.domainIdTarget.value
    const row = this.optionTargets.find((option) => option.dataset.domainId === selectedId)
    return row ? row.dataset.hostname : ''
  }

  updateCnameHost (hostname) {
    const cell = this.element.closest('form')?.querySelector('[data-domain-help-host]')
    if (cell) cell.textContent = hostname || 'yourdomain.com'
  }

  save () {
    this.element.closest('form').requestSubmit()
  }

  memberPath (id) {
    return `${this.accountPathValue}/${id}`
  }

  request (method, url, params = {}) {
    const csrfToken = document.querySelector('meta[name=csrf-token]')?.content
    const body = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => body.append(`domain[${key}]`, value))

    return fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'text/vnd.turbo-stream.html, text/html',
        'X-CSRF-Token': csrfToken || ''
      },
      body: method === 'DELETE' ? undefined : body.toString()
    })
  }

  async renderStream (response) {
    const body = await response.text()

    if (!response.ok || response.redirected ||
        !response.headers.get('Content-Type')?.includes('turbo-stream')) {
      this.notify(response.ok ? 'Something went wrong. Reload the page and try again.' : body)
      return false
    }

    Turbo.renderStreamMessage(body)
    return true
  }

  notify (message) {
    document.dispatchEvent(new CustomEvent('toast:show', {
      detail: { message: message || 'Something went wrong', variant: 'error' }
    }))
  }
}
